import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getMockupErrorFormats, IErrorFormatRow, IErrorFormatsResponse } from '../../api/healthCheck';
import { drawDonut, drawDonutCompare, type ISlice } from '../../lib/mockupLogsDashboard/charts';
import { delta, deltaBadgeClass, fmt, fmtPct } from '../../lib/mockupLogsDashboard/utils';
import type { DashboardPalette } from '../../lib/mockupLogsDashboard/palette';

interface Props {
  start: string;
  end: string;
  /** Comparison range; empty strings when compare is off. */
  cstart?: string;
  cend?: string;
  resizeTick?: number;
  themeMode?: 'light' | 'dark';
  palette: DashboardPalette;
}

const TIP =
  'Errored mockup events grouped by the source image format the customer uploaded (as reported by the server). Formats are read from the response as-is, so new formats appear automatically. Share = count / total errors in the range.';

/**
 * Fixed categorical order (never cycled): the top formats get the palette hues in rank order,
 * everything past the palette is folded into "other".
 */
function foldForDonut(rows: IErrorFormatRow[], colors: string[], labelColors: string[], otherColor: string, otherLabelColor: string): ISlice[] {
  const maxSlices = colors.length;
  const top = rows.slice(0, maxSlices - (rows.length > maxSlices ? 1 : 0));
  const slices: ISlice[] = top.map((r, i) => ({ label: r.format, value: r.count, color: colors[i], labelColor: labelColors[i] }));
  if (rows.length > top.length) {
    const rest = rows.slice(top.length).reduce((s, r) => s + r.count, 0);
    slices.push({ label: 'other', value: rest, color: otherColor, labelColor: otherLabelColor });
  }
  return slices;
}

function DeltaInline({ curr, prev }: { curr: number; prev: number | null | undefined }) {
  const d = delta(curr, prev ?? undefined);
  if (d === null || Number.isNaN(d)) return null;
  const cls = deltaBadgeClass(curr, prev ?? undefined, { inverted: true });
  const arrow = d > 0.05 ? '▲' : d < -0.05 ? '▼' : '–';
  return (
    <span className={`delta-inline ${cls}`}>
      {arrow}
      {Math.abs(d).toFixed(1)}%
    </span>
  );
}

function InfoTip({ text }: { text: string }) {
  return (
    <button type="button" className="info-btn" data-tip={text} aria-label={text}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2" />
        <circle cx="7" cy="4.2" r="0.85" fill="currentColor" />
        <path d="M7 6.5 V10.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    </button>
  );
}

const MockupLogsErrorFormats: React.FC<Props> = ({ start, end, cstart = '', cend = '', resizeTick = 0, themeMode = 'light', palette }) => {
  const [curr, setCurr] = useState<IErrorFormatsResponse | null>(null);
  const [prev, setPrev] = useState<IErrorFormatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const genRef = useRef(0);
  const refDonut = useRef<HTMLCanvasElement>(null);

  const hasPrev = !!(cstart && cend);

  useEffect(() => {
    if (!start || !end) return;
    const gen = ++genRef.current;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [c, p] = await Promise.all([
          getMockupErrorFormats({ createdAt_min: start, createdAt_max: end }),
          hasPrev ? getMockupErrorFormats({ createdAt_min: cstart, createdAt_max: cend }) : Promise.resolve(null),
        ]);
        if (genRef.current !== gen) return;
        setCurr(c);
        setPrev(p);
      } catch (err: unknown) {
        if (genRef.current !== gen) return;
        // Do not keep another range's rows next to an error message.
        setCurr(null);
        setPrev(null);
        setError(err instanceof Error ? err.message : 'Failed to load error formats');
      } finally {
        if (genRef.current === gen) setLoading(false);
      }
    })();
  }, [start, end, cstart, cend, hasPrev]);

  const rows = useMemo(() => curr?.result ?? [], [curr]);
  const total = curr?.totalCount ?? 0;
  const prevByFormat = useMemo(() => {
    const m = new Map<string, number>();
    prev?.result.forEach((r) => m.set(r.format, r.count));
    return m;
  }, [prev]);
  const prevTotal = prev?.totalCount ?? 0;
  const maxCount = rows.length ? Math.max(...rows.map((r) => r.count)) : 0;

  // Formats that exist only in the previous period still deserve a row.
  const tableRows = useMemo(() => {
    const seen = new Set(rows.map((r) => r.format));
    const extra: IErrorFormatRow[] = [];
    prev?.result.forEach((r) => {
      if (!seen.has(r.format)) extra.push({ format: r.format, count: 0 });
    });
    return [...rows, ...extra];
  }, [rows, prev]);

  const slices = useMemo(() => foldForDonut(rows, palette.formats, palette.formatsLabel, palette.neutral, palette.label.neutral), [rows, palette]);

  useLayoutEffect(() => {
    const canvas = refDonut.current;
    if (!canvas) return;
    if (!slices.length) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    if (hasPrev && prev) {
      const prevSlices = slices.map((s) => {
        if (s.label === 'other') {
          const known = new Set(slices.filter((x) => x.label !== 'other').map((x) => x.label));
          return { value: prev.result.filter((r) => !known.has(r.format)).reduce((sum, r) => sum + r.count, 0) };
        }
        return { value: prevByFormat.get(s.label || '') ?? 0 };
      });
      drawDonutCompare(canvas, slices, prevSlices, { centerLabel: fmt(total), centerSub: 'errors' });
    } else {
      drawDonut(canvas, slices, { donut: true, centerLabel: fmt(total), centerSub: 'errors' });
    }
  }, [slices, prev, prevByFormat, hasPrev, total, resizeTick, themeMode]);

  const totalDelta = hasPrev && prev ? delta(total, prevTotal) : null;

  return (
    <>
      <div className="section-title">
        Error formats <InfoTip text={TIP} />
      </div>
      <div className="grid grid-2">
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Errors by image format</h3>
              </div>
              <div className="sub">
                {loading ? 'Loading…' : `${fmt(total)} errored uploads · ${rows.length} format${rows.length === 1 ? '' : 's'}`}
                {hasPrev && prev ? ` · prev ${fmt(prevTotal)}` : ''}
              </div>
            </div>
            {totalDelta !== null ? (
              <span className={`pill ${totalDelta > 0 ? 'danger' : ''}`}>
                <span className="dot" />
                {`${totalDelta > 0 ? '+' : ''}${totalDelta.toFixed(1)}% vs prev`}
              </span>
            ) : null}
          </div>
          <div className="chart-body" style={{ height: 220 }}>
            {error ? (
              <p className="status-line error" style={{ margin: 0 }}>{error}</p>
            ) : !loading && !rows.length ? (
              <p className="muted" style={{ margin: 0 }}>No errored uploads in this range.</p>
            ) : (
              <canvas ref={refDonut} role="img" aria-label="Errors by image format" style={{ width: '100%', height: '100%' }} />
            )}
          </div>
          {slices.length > 0 && (
            <div className="legend">
              {slices.map((s) => (
                <span key={s.label}>
                  <span className="swatch" style={{ background: s.color }} />
                  {s.label} <strong>{total ? fmtPct((s.value / total) * 100, 1) : '—'}</strong>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Format</th>
                <th className="num">Errors</th>
                <th className="num">Share</th>
                {hasPrev && <th className="num">Prev</th>}
                <th style={{ width: '38%' }}>Distribution</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r, i) => {
                const share = total ? (r.count / total) * 100 : 0;
                const maxSlices = palette.formats.length;
                const topCount = maxSlices - (rows.length > maxSlices ? 1 : 0);
                const color = i < topCount && i < rows.length ? palette.formats[i] : palette.neutral;
                const pc = prevByFormat.get(r.format);
                return (
                  <tr key={r.format}>
                    <td>
                      <span className="swatch" style={{ background: color, marginRight: 6 }} />
                      <strong>{r.format}</strong>
                    </td>
                    <td className="num">
                      {fmt(r.count)}
                      {hasPrev && pc !== undefined ? <DeltaInline curr={r.count} prev={pc} /> : null}
                    </td>
                    <td className="num">{fmtPct(share, 1)}</td>
                    {hasPrev && <td className="num">{pc !== undefined ? fmt(pc) : '—'}</td>}
                    <td>
                      <div className="stack" title={`${r.format}: ${r.count}`}>
                        <span style={{ width: `${maxCount ? (r.count / maxCount) * 100 : 0}%`, background: color }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && !tableRows.length && (
                <tr>
                  <td colSpan={hasPrev ? 5 : 4} className="muted">
                    {error ? error : 'No data for this range.'}
                  </td>
                </tr>
              )}
              {loading && !tableRows.length && (
                <tr>
                  <td colSpan={hasPrev ? 5 : 4} className="muted">Loading…</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default MockupLogsErrorFormats;

